/**
 * Two-hand move / turn / resize of the selected object in VR build mode.
 *
 * Pinch ON the selected shape or model with both hands — each pinch has to
 * start inside its box (plus a small margin) or with that hand's ray on it —
 * and then, in one gesture:
 *   - move both hands together and it moves (the midpoint between them)
 *   - swing them around each other and it turns about its vertical axis
 *   - pull them apart / together and it grows / shrinks, keeping proportions
 * A one-hand grab (VRGrab) hands over the moment the second pinch lands on the
 * object. Pinches that start on the palette or any panel, the pinch that drops
 * a held item, or anything while an item is in your hand never start it.
 *
 * Let go with either hand to finish. With Snap on the result is rounded (grid
 * position, 15° turns, 10 % size steps). It's saved, broadcast and added to
 * undo only if something actually changed — the same store actions and
 * broadcastObjectUpdate the desktop gizmo uses — and never broadcast without
 * a world id.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { useWorldStore } from '../../../stores/worldStore';
import { broadcastObjectUpdate } from '../../../services/worldBroadcast';
import { hands, handMode, pinch, type HandSide } from '../hands/handTracking';
import { PINCH_SETTLE_MS } from '../hands/pinch';
import { useVRBuild } from './vrBuildState';
import { rayHitsUi } from './vrUiRay';
import { solveTwoHand, snapK, snapPos, snapYaw, pinchOnBox, proportionalScale, objectBox, SCALE_MIN, SCALE_MAX, type TwoHandResult } from './vrBuildMath';

type Kind = 'primitive' | 'content';

/** How far outside the object's box (m) a pinch still counts as "on" it. */
const MARGIN = .12;
/** Longest ray (m) for a pinch to count as pointing at the object. */
const FAR = 12;
const EPS = 1e-3;

/**
 * The selected shape or model's Object3D. Shapes are found by their stable
 * name (`primitive-<id>`): a shape's userData is replaced on re-render, so
 * `userData.objectId` isn't reliable for them. Anything that isn't a shape or a
 * model (lights, screens…) is not hand-grabbable here — writing it back as
 * content would invent a fake content entry.
 */
export function findSelected(scene: THREE.Object3D, id: string): { obj: THREE.Object3D; kind: Kind } | null {
  const st = useWorldStore.getState();
  if (st.primitives[id]) {
    if (st.primitives[id].isLocked) return null;
    const obj = scene.getObjectByName(`primitive-${id}`);
    return obj ? { obj, kind: 'primitive' } : null;
  }
  const item = st.contentItems[id] as any;
  if (!item || item.isLocked) return null;
  let found: { obj: THREE.Object3D; kind: Kind } | null = null;
  scene.traverse((o) => {
    if (found || o.userData?.objectId !== id) return;
    found = { obj: o, kind: 'content' };
  });
  return found;
}

const near = (a: number, b: number) => Math.abs(a - b) < EPS;

export default function VRHandManipulator({ worldId }: { worldId: string }) {
  const { scene } = useThree();
  const origin = useXR((s) => s.origin);
  const canBuild = useWorldStore((s) => s.canBuild);

  /** Which pinch (by count) each hand last started, and whether it started on the selection (its id). */
  const seen = useRef<Record<HandSide, number>>({ left: pinch.left.count, right: pinch.right.count });
  const startOn = useRef<Record<HandSide, string | null>>({ left: null, right: null });
  const T = useMemo(() => ({
    id: '', kind: 'primitive' as Kind, obj: null as THREE.Object3D | null,
    start: { left: new THREE.Vector3(), right: new THREE.Vector3(), pos: new THREE.Vector3(), yaw: 0 },
    s0: new THREE.Vector3(),
    out: { pos: new THREE.Vector3(), yaw: 0, k: 1 } as TwoHandResult,
    settleUntil: 0,
  }), []);
  const two = useRef<typeof T | null>(null);
  const v = useMemo(() => ({
    a: new THREE.Vector3(), b: new THREE.Vector3(), o: new THREE.Vector3(), d: new THREE.Vector3(), w: new THREE.Vector3(),
    box: new THREE.Box3(), box2: new THREE.Box3(), ray: new THREE.Ray(), rc: new THREE.Raycaster(),
  }), []);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as any).__gxTwo = { startOn, seen, two };
    return () => { delete (window as any).__gxTwo; };
  }, []);

  const pinchPoint = (side: HandSide, out: THREE.Vector3) =>
    out.copy(hands[side].thumbTip).add(hands[side].indexTip).multiplyScalar(.5).applyMatrix4(origin!.matrixWorld);

  /** The object a two-hand gesture would take: what a one-hand grab holds, else the selection. */
  const targetId = () => useVRBuild.getState().grabbedId ?? useWorldStore.getState().selectedObjectId;

  /** Did this hand's pinch just start on the target object (and not on a panel)? */
  const startsOnSelection = (side: HandSide): string | null => {
    const sel = targetId();
    const h = hands[side];
    if (!sel || !h.present || pinch[side].consumed) return null;
    const f = findSelected(scene, sel);
    if (!f) return null;
    objectBox(f.obj, v.box);
    if (v.box.isEmpty()) return null;
    const mw = origin!.matrixWorld;
    pinchPoint(side, v.a);
    v.o.copy(h.rayOrigin).applyMatrix4(mw);
    v.d.copy(h.rayDir).transformDirection(mw);
    v.ray.set(v.o, v.d);
    if (!pinchOnBox(v.box, v.a, v.ray, FAR, MARGIN, v.box2, v.w)) return null;
    // Pointing at it from afar but a panel is in the way → that pinch was for the panel.
    v.box2.copy(v.box).expandByScalar(MARGIN);
    if (!v.box2.containsPoint(v.a)) {
      const hitDist = v.w.distanceTo(v.o);
      if (rayHitsUi(v.rc, scene, v.o, v.d, hitDist)) return null;
    }
    return sel;
  };

  /** Snap (if on), then save + broadcast — only if the transform differs from the stored one. */
  const finish = () => {
    const t = two.current;
    two.current = null;
    const b = useVRBuild.getState();
    b.setTwoHand(false);
    b.setHandling(false);
    b.setHandlingSide(null);
    (window as any).__vrGrabbing = false;
    if (!t || !t.obj) return;
    const obj = t.obj;
    const st = useWorldStore.getState();
    const snap = st.snapSettings;
    if (snap.grid) {
      const gs = snap.gridSize || .5;
      obj.getWorldPosition(v.w);
      snapPos(v.w, gs);
      if (obj.parent) obj.parent.worldToLocal(v.w);
      obj.position.copy(v.w);
      obj.rotation.y = snapYaw(obj.rotation.y);
      const k = snapK(t.out.k);
      if (t.kind === 'content') obj.scale.setScalar(THREE.MathUtils.clamp(t.s0.x * k, .005, SCALE_MAX));
      else proportionalScale(t.s0, k, obj.scale, SCALE_MIN, SCALE_MAX);
    }
    const position: [number, number, number] = [+obj.position.x.toFixed(3), +obj.position.y.toFixed(3), +obj.position.z.toFixed(3)];
    const rotation: [number, number, number] = [obj.rotation.x, obj.rotation.y, obj.rotation.z];
    if (t.kind === 'primitive') {
      const cur = st.primitives[t.id];
      if (!cur) return;
      const scale: [number, number, number] = [+obj.scale.x.toFixed(3), +obj.scale.y.toFixed(3), +obj.scale.z.toFixed(3)];
      const same = [0, 1, 2].every((i) => near(cur.position[i], position[i]) && near(cur.rotation[i], rotation[i]) && near(cur.scale[i], scale[i]));
      if (same) return;
      const updates = { position, rotation, scale };
      st.updatePrimitive(t.id, updates);
      if (worldId) broadcastObjectUpdate(worldId, 'primitive', t.id, updates);
    } else {
      const cur = st.contentItems[t.id] as any;
      if (!cur) return;
      const scale = +obj.scale.x.toFixed(4);
      const cs = typeof cur.scale === 'number' ? cur.scale : (cur.scale?.[0] ?? 1);
      const same = [0, 1, 2].every((i) => near(cur.position?.[i] ?? 0, position[i]) && near(cur.rotation?.[i] ?? 0, rotation[i])) && Math.abs(cs - scale) < 1e-4;
      if (same) return;
      const updates = { position, rotation, scale };
      st.updateContentItem(t.id, updates as any);
      if (worldId) broadcastObjectUpdate(worldId, 'content', t.id, updates);
    }
  };

  useFrame(() => {
    if (!canBuild || !origin || !handMode.active) {
      if (two.current) finish();
      seen.current.left = pinch.left.count; seen.current.right = pinch.right.count;
      return;
    }
    origin.updateMatrixWorld();
    const build = useVRBuild.getState();
    const st = useWorldStore.getState();

    // Where did each new pinch start? (Runs after VRGrab, so a pinch that grabbed something has already selected it.)
    for (const side of ['left', 'right'] as HandSide[]) {
      if (pinch[side].count === seen.current[side]) continue;
      seen.current[side] = pinch[side].count;
      startOn.current[side] = startsOnSelection(side);
    }

    const L = pinch.left.held, R = pinch.right.held;

    // ---- During the gesture ----
    const t = two.current;
    if (t) {
      if (!L || !R || !t.obj || !t.obj.parent) { finish(); return; }
      // A hand in its release grace (fingers opening, or a blip): hold still until it's back or gone.
      if (pinch.left.releasing || pinch.right.releasing) return;
      pinchPoint('left', v.a); pinchPoint('right', v.b);
      if (performance.now() < t.settleUntil) {
        // Fingers still closing: re-take where the hands are, leave the object be.
        t.start.left.copy(v.a); t.start.right.copy(v.b);
        return;
      }
      solveTwoHand(t.start, v.a, v.b, t.out);
      v.w.copy(t.out.pos);
      if (t.obj.parent) t.obj.parent.worldToLocal(v.w);
      t.obj.position.copy(v.w);
      t.obj.rotation.y = t.out.yaw;
      if (t.kind === 'content') t.obj.scale.setScalar(THREE.MathUtils.clamp(t.s0.x * t.out.k, .005, SCALE_MAX));
      else proportionalScale(t.s0, t.out.k, t.obj.scale, SCALE_MIN, SCALE_MAX);
      return;
    }

    // ---- Start: both pinches began on the selection, neither used by something else ----
    const sel = targetId();
    if (!L || !R || !sel || !pinch.left.free || !pinch.right.free) return;
    if (startOn.current.left !== sel || startOn.current.right !== sel) return;
    if (build.held || build.placing || build.resizing || st.activeTool === 'place') return;
    const f = findSelected(scene, sel);
    if (!f) return;
    pinchPoint('left', T.start.left); pinchPoint('right', T.start.right);
    f.obj.getWorldPosition(T.start.pos);
    T.start.yaw = f.obj.rotation.y;
    T.s0.copy(f.obj.scale);
    T.out.pos.copy(T.start.pos); T.out.yaw = T.start.yaw; T.out.k = 1;
    T.id = sel; T.kind = f.kind; T.obj = f.obj;
    T.settleUntil = performance.now() + PINCH_SETTLE_MS;
    if (st.selectedObjectId !== sel) st.setSelectedObjectId(sel); // a stray pinch-click may have selected something behind it
    two.current = T;
    build.setTwoHand(true);
    build.setHandling(true);
    build.setHandlingSide(null);
    (window as any).__vrGrabbing = true; // stray pinch-clicks don't select things meanwhile (WorldBuilder)
  });

  return null;
}
